/**
 * @file TextArea component
 * @author liyanan19
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useMemo } from 'react';
import RcTextArea from 'rc-textarea';
import classNames from 'classnames';
import MultiToneClear from 'acud-icon/lib/icons/MultiToneClear';
import { ConfigContext } from '../config-provider';
import useMergedState from '../_util/hooks/useMergedState';
import { resolveTargetValue } from './Input';
import Popover from '../popover';
// const TextArea:React.FC<TextAreaProps> = props => {
const TextArea = React.forwardRef((props, ref) => {
    const { prefixCls: customizePrefixCls, placeholder = '请输入内容', className, style, width, limitLength, readonly, autoSize, disabled = false, onPressEnter, onKeyDown, onFocus, onBlur, allowClear = true, forbidIfLimit = false, warningPopover, warningText, onCompositionStart, onCompositionEnd } = props, rest = __rest(props, ["prefixCls", "placeholder", "className", "style", "width", "limitLength", "readonly", "autoSize", "disabled", "onPressEnter", "onKeyDown", "onFocus", "onBlur", "allowClear", "forbidIfLimit", "warningPopover", "warningText", "onCompositionStart", "onCompositionEnd"]);
    const [value, setValue] = useMergedState(props.defaultValue, {
        value: props.value,
    });
    const [clearIconColor, setClearIconColor] = useState('#D4D6D9');
    const currentLength = useMemo(() => (value === null || value === void 0 ? void 0 : value.length) || 0, [value]);
    const [focusClass, setFocusClass] = useState('');
    const composedRef = React.useRef(false);
    const [composingValue, setComposingValue] = useState('');
    const handleOnChange = (e) => {
        var _a;
        if (readonly) {
            return;
        }
        const newVal = e.target.value;
        if (composedRef.current) {
            setComposingValue(newVal);
        }
        else {
            if (forbidIfLimit
                && limitLength
                && newVal.length >= limitLength) {
                setComposingValue(newVal.slice(0, limitLength));
                setValue(newVal.slice(0, limitLength));
                e.target.value = e.target.value.slice(0, limitLength);
            }
            setComposingValue(newVal);
            setValue(newVal);
            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, e);
        }
    };
    const handleOnKeyDown = (e) => {
        if (onPressEnter && e.key === 'Enter') {
            onPressEnter(e);
            // setValue('');
        }
        if (onKeyDown) {
            onKeyDown(e);
        }
    };
    const handleFocus = (e) => {
        if (readonly) {
            return;
        }
        setFocusClass('focus');
        if (onFocus) {
            onFocus(e);
        }
    };
    const handleBlur = (e) => {
        if (readonly) {
            return;
        }
        setFocusClass('');
        if (onBlur) {
            onBlur(e);
        }
    };
    const hoverClear = () => {
        setClearIconColor('#A7ABB5');
    };
    const clearMouseLeave = () => {
        setClearIconColor('#D4D6D9');
    };
    const handleOnCompositionStart = e => {
        composedRef.current = true;
        onCompositionStart === null || onCompositionStart === void 0 ? void 0 : onCompositionStart(e);
    };
    const handleOnCompositionEnd = e => {
        if (composedRef.current) {
            composedRef.current = false;
            handleOnChange(e);
        }
        onCompositionEnd === null || onCompositionEnd === void 0 ? void 0 : onCompositionEnd(e);
    };
    const { getPrefixCls } = React.useContext(ConfigContext);
    const prefixCls = getPrefixCls('input-textarea', customizePrefixCls);
    const classes = classNames(prefixCls, {
        [`${prefixCls}-autosize`]: autoSize,
        [`${prefixCls}-readonly`]: readonly,
        [`${prefixCls}-span-focus`]: focusClass,
        [`${prefixCls}-limit`]: currentLength > limitLength,
        [`${prefixCls}-no-limit`]: !!limitLength,
        [`${prefixCls}-no-clear`]: !allowClear,
        [`${prefixCls}-error`]: warningPopover
    });
    const limitClassName = classNames({
        [`${prefixCls}-warning`]: currentLength > limitLength
    });
    const limitBoxClassName = classNames({
        [`${prefixCls}-limit-box-readonly`]: readonly
    });
    const clearContent = e => {
        // setValue('');
        // 参考 input
        handleOnChange(resolveTargetValue(e, ''));
    };
    const innerValue = composedRef.current ? composingValue : value !== null && value !== void 0 ? value : '';
    const textAreaContent = (<div className={classNames(`${prefixCls}-outer`, className)} style={{ width: width }}>
            <RcTextArea {...rest} ref={ref} style={style} placeholder={placeholder} className={classNames(classes)} onChange={handleOnChange} onKeyDown={handleOnKeyDown} onFocus={handleFocus} onBlur={handleBlur} value={innerValue} autoSize={autoSize} disabled={disabled} onCompositionStart={handleOnCompositionStart} onCompositionEnd={handleOnCompositionEnd}/>
            {value && allowClear && !readonly && (<div onClick={clearContent} className={`${prefixCls}-outer-icon-clear`} onMouseEnter={hoverClear} onMouseLeave={clearMouseLeave}>
                    <MultiToneClear width={16} color={clearIconColor}/>
                </div>)}
            {limitLength && (<div className={classNames(limitBoxClassName, `${prefixCls}-limit-box`)}>
                    <span className={classNames(limitClassName, 'limit')}>
                        {currentLength}/{limitLength}
                    </span>
                </div>)}
        </div>);
    return (<Popover content={<span className={`${prefixCls}-warning-popover-text`}>
                    {warningText}
                </span>} {...warningPopover && !!warningText ? {} : { visible: false }} placement="bottomLeft">
            {textAreaContent}
        </Popover>);
});
export default TextArea;
